import type {
  LibraryCourseNode,
  LibraryModuleNode,
  LibraryResponse,
} from '@/api/lcLearning'
import {
  classifyCourseTitle,
  parseCoreCourseIdentity,
  type CoreCourseIdentity,
  type CourseTitleType,
} from './libraryModel'

export type JourneyChapterStatus = 'complete' | 'current' | 'locked'

export interface JourneyChapter {
  id: number
  title: string
  completedLessons: number
  totalLessons: number
  status: JourneyChapterStatus
}

export interface JourneyCourseSection {
  id: number
  title: string
  completedLessons: number
  totalLessons: number
  progressPercent: number
  chapters: JourneyChapter[]
}

export interface JourneyTab {
  key: string
  title: string
  courseTitleType: CourseTitleType
  coreCode: CoreCourseIdentity['code'] | null
  completedLessons: number
  totalLessons: number
  progressPercent: number
  courses: JourneyCourseSection[]
}

export interface LearningJourneyData {
  curriculumTitle: string
  tabs: JourneyTab[]
}

interface ChapterDraft extends Omit<JourneyChapter, 'status'> {
  hasInProgress: boolean
}

interface TabDraft {
  key: string
  title: string
  courseTitleType: CourseTitleType
  coreCode: CoreCourseIdentity['code'] | null
  courses: Array<{
    id: number
    title: string
    chapters: ChapterDraft[]
  }>
}

function toChapterDraft(module: LibraryModuleNode): ChapterDraft | null {
  if (module.lessons.length === 0) return null

  let completedLessons = 0
  let hasInProgress = false
  for (const lesson of module.lessons) {
    if (lesson.status === 'completed') completedLessons += 1
    if (lesson.status === 'in_progress') hasInProgress = true
  }

  return {
    id: module.contentId,
    title: module.title,
    completedLessons,
    totalLessons: module.lessons.length,
    hasInProgress,
  }
}

function getTabIdentity(
  course: LibraryCourseNode,
): Pick<TabDraft, 'key' | 'title' | 'courseTitleType' | 'coreCode'> {
  const identity = parseCoreCourseIdentity(course.title)
  if (identity) {
    return {
      key: identity.journeyGroupKey ?? `course-${course.contentId}`,
      title: course.title,
      courseTitleType: 'core',
      coreCode: identity.code,
    }
  }
  return {
    key: `course-${course.contentId}`,
    title: course.title,
    courseTitleType: classifyCourseTitle(course.title),
    coreCode: null,
  }
}

function finalizeTab(tab: TabDraft): JourneyTab {
  const chapterDrafts = tab.courses.flatMap((course) => course.chapters)
  let currentIndex = chapterDrafts.findIndex((chapter) => chapter.hasInProgress)
  if (currentIndex < 0) {
    currentIndex = chapterDrafts.findIndex(
      (chapter) => chapter.completedLessons < chapter.totalLessons,
    )
  }

  let chapterIndex = 0
  const courses = tab.courses
    .map<JourneyCourseSection>((course) => {
      let courseCompletedLessons = 0
      let courseTotalLessons = 0
      const chapters = course.chapters.map<JourneyChapter>((chapter) => {
        const isComplete = chapter.completedLessons === chapter.totalLessons
        const status: JourneyChapterStatus = isComplete
          ? 'complete'
          : chapterIndex === currentIndex
            ? 'current'
            : 'locked'
        chapterIndex += 1
        courseCompletedLessons += chapter.completedLessons
        courseTotalLessons += chapter.totalLessons
        return {
          id: chapter.id,
          title: chapter.title,
          completedLessons: chapter.completedLessons,
          totalLessons: chapter.totalLessons,
          status,
        }
      })
      return {
        id: course.id,
        title: course.title,
        completedLessons: courseCompletedLessons,
        totalLessons: courseTotalLessons,
        progressPercent: courseTotalLessons === 0
          ? 0
          : Math.round((courseCompletedLessons / courseTotalLessons) * 100),
        chapters,
      }
    })
    .filter((course) => course.chapters.length > 0)
  const completedLessons = courses.reduce(
    (sum, course) => sum + course.completedLessons,
    0,
  )
  const totalLessons = courses.reduce(
    (sum, course) => sum + course.totalLessons,
    0,
  )

  return {
    key: tab.key,
    title: tab.title,
    courseTitleType: tab.courseTitleType,
    coreCode: tab.coreCode,
    completedLessons,
    totalLessons,
    progressPercent: totalLessons === 0 ? 0 : Math.round((completedLessons / totalLessons) * 100),
    courses,
  }
}

export function toLearningJourneyData(response: LibraryResponse): LearningJourneyData {
  const tabsByKey = new Map<string, TabDraft>()

  for (const course of response.curriculum.courses) {
    const identity = getTabIdentity(course)
    const existing = tabsByKey.get(identity.key)
    const tab = existing ?? { ...identity, courses: [] }
    tab.courses.push({
      id: course.contentId,
      title: course.title,
      chapters: course.modules
        .map(toChapterDraft)
        .filter((chapter): chapter is ChapterDraft => chapter !== null),
    })
    if (!existing) tabsByKey.set(identity.key, tab)
  }

  return {
    curriculumTitle: response.curriculum.title,
    tabs: Array.from(tabsByKey.values(), finalizeTab),
  }
}

export function findInitialJourneyTabKey(tabs: JourneyTab[]): string | null {
  return tabs.find((tab) => tab.totalLessons > 0)?.key ?? tabs[0]?.key ?? null
}
